import { useTranslation } from "react-i18next";

/*
 * TimeseriesConfigBar 역할:
 * - 시계열 비교에 사용할 이미지 타입과 파일 선택 UI를 한곳에 모아 렌더링한다.
 * - 비교 후보 추가, 현재 선택 파일 제거 같은 상단 제어 액션을 담당한다.
 */
export default function TimeseriesConfigBar({
  imageType,
  onImageTypeChange,
  imageTypeOptions,
  candidateOptionFields,
  addDropdownRef,
  isAddDropdownOpen,
  onToggleAddDropdown,
  isCandidateLoading,
  addableFiles,
  selectedPendingCandidates,
  buildCandidateOptionText,
  buildCandidateOptionParts,
  getUploadId,
  pendingUploadIds,
  onPendingCandidateToggle,
  onAddFile,
  selectedFiles,
  currentUploadId,
  onRemoveFile,
  getDisplayText,
}) {
  const { t } = useTranslation();
  const candidateColumnClassMap = {
    uploaded_at: "candidateColUploadedAt",
    file_name: "candidateColFileName",
    facility_type: "candidateColFacilityType",
    facility_name: "candidateColFacilityName",
    part_name: "candidateColPartName",
    location: "candidateColLocation",
  };
  const getCandidateColumnClass = (key) =>
    candidateColumnClassMap[key] || "candidateColAuto";

  return (
    <div className="equipmentTimeseriesReport configBar">
      <div className="equipmentTimeseriesReport configField">
        <strong>
          {t("imageFileIndex.timeseries.config.imageType", {
            defaultValue: "이미지 종류",
          })}
        </strong>
        <select value={imageType} onChange={onImageTypeChange}>
          {imageTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div className="equipmentTimeseriesReport configField isWide">
        <strong>
          {t("imageFileIndex.timeseries.config.selectedData", {
            defaultValue: "선택한 파일",
          })}
        </strong>
        <div className="equipmentTimeseriesReport selectedFileList">
          {selectedFiles.map((item) => {
            const uploadId = String(item?.upload_id ?? "");
            const isCurrent = uploadId === currentUploadId;

            return (
              <div key={uploadId} className="equipmentTimeseriesReport selectedFileChip">
                <span className="equipmentTimeseriesReport selectedFileName">
                  {isCurrent
                    ? t("imageFileIndex.timeseries.config.currentFile", {
                        name: getDisplayText(item?.file_name),
                        defaultValue: "{{name}} (현재 파일)",
                      })
                    : getDisplayText(item?.file_name)}
                </span>
                {!isCurrent ? (
                  <button
                    type="button"
                    className="equipmentTimeseriesReport removeIconButton"
                    onClick={() => onRemoveFile(uploadId)}
                    aria-label={t("imageFileIndex.timeseries.config.removeFile", {
                      name: getDisplayText(item?.file_name),
                      defaultValue: "{{name}} 비교에서 제외",
                    })}
                    title={t("imageFileIndex.timeseries.config.removeFile", {
                      name: getDisplayText(item?.file_name),
                      defaultValue: "{{name}} 비교에서 제외",
                    })}
                  >
                    <svg
                      className="equipmentTimeseriesReport removeIconSvg"
                      viewBox="0 0 12 12"
                      aria-hidden="true"
                    >
                      <path d="M2 2L10 10" />
                      <path d="M10 2L2 10" />
                    </svg>
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="equipmentTimeseriesReport configField isWide">
        <strong>
          {t("imageFileIndex.timeseries.config.addData", {
            defaultValue: "비교 파일 추가",
          })}
        </strong>
        <div
          className="equipmentTimeseriesReport addControl"
          ref={addDropdownRef}
        >
          <div className="equipmentTimeseriesReport addControlRow">
            <div className="equipmentTimeseriesReport addDropdownBox">
              <button
                type="button"
                className="equipmentTimeseriesReport candidateDropdownButton"
                onClick={onToggleAddDropdown}
                disabled={isCandidateLoading || addableFiles.length === 0}
                aria-haspopup="listbox"
                aria-expanded={isAddDropdownOpen}
              >
                {selectedPendingCandidates.length > 0 ? (
                  <span className="equipmentTimeseriesReport candidatePlaceholder">
                    {t("imageFileIndex.timeseries.config.selectedCount", {
                      count: selectedPendingCandidates.length,
                      defaultValue: "{{count}}개 파일 선택됨",
                    })}
                  </span>
                ) : (
                  <span className="equipmentTimeseriesReport candidatePlaceholder">
                    {addableFiles.length === 0
                      ? t("imageFileIndex.timeseries.config.noAddableFiles", {
                          defaultValue: "추가 가능한 파일 없음",
                        })
                      : t("imageFileIndex.timeseries.config.selectFileToAdd", {
                          defaultValue: "추가할 파일 선택",
                        })}
                  </span>
                )}
                <span
                  className="equipmentTimeseriesReport candidateDropdownArrow"
                  aria-hidden
                >
                  {isAddDropdownOpen ? "▲" : "▼"}
                </span>
              </button>
            </div>

            <button
              type="button"
              className="equipmentTimeseriesReport addActionButton"
              onClick={onAddFile}
              disabled={pendingUploadIds.length === 0}
            >
              {pendingUploadIds.length > 0
                ? t("imageFileIndex.timeseries.config.addSelectedData", {
                    count: pendingUploadIds.length,
                    defaultValue: "선택한 파일 {{count}}개 추가",
                  })
                : t("imageFileIndex.timeseries.config.addDataButton", {
                    defaultValue: "비교 파일 추가",
                  })}
            </button>
          </div>

          {isAddDropdownOpen && addableFiles.length > 0 ? (
            <div className="equipmentTimeseriesReport candidateDropdownPanel">
              <div className="equipmentTimeseriesReport candidateDropdownScroll">
                <div className="equipmentTimeseriesReport candidateGuideGrid">
                  <span className="equipmentTimeseriesReport candidateGuideCheckSpacer candidateColSelect">
                    {t("imageFileIndex.timeseries.config.select", {
                      defaultValue: "선택",
                    })}
                  </span>
                  {candidateOptionFields.map((field) => (
                    <span
                      key={field.key}
                      className={`equipmentTimeseriesReport candidateGuideItem ${getCandidateColumnClass(
                        field.key,
                      )}`}
                    >
                      {field.label}
                    </span>
                  ))}
                </div>

                <ul
                  className="equipmentTimeseriesReport candidateDropdownMenu"
                  role="listbox"
                >
                  {addableFiles.map((candidate) => {
                    const candidateId = getUploadId(candidate);

                    return (
                      <li
                        key={candidateId}
                        role="option"
                        aria-selected={pendingUploadIds.includes(candidateId)}
                      >
                        <button
                          type="button"
                          className={`equipmentTimeseriesReport candidateDropdownItem${
                            pendingUploadIds.includes(candidateId)
                              ? " isSelected"
                              : ""
                          }`}
                          onClick={() => onPendingCandidateToggle(candidateId)}
                          title={buildCandidateOptionText(candidate)}
                        >
                          <span
                            className={`equipmentTimeseriesReport candidateCheck candidateColSelect${
                              pendingUploadIds.includes(candidateId)
                                ? " isChecked"
                                : ""
                            }`}
                            aria-hidden="true"
                          />
                          {buildCandidateOptionParts(candidate).map((part) => (
                            <span
                              key={`${candidateId}-${part.label}`}
                              className={`equipmentTimeseriesReport candidateOptionField ${getCandidateColumnClass(
                                part.key,
                              )}`}
                            >
                              <span className="equipmentTimeseriesReport candidateOptionFieldText">
                                {part.value}
                              </span>
                            </span>
                          ))}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
